/**
 * API client for the website-audit data served on the one Audit page (the
 * standalone Website Audit page is retired; its sheets live in the Audit
 * page's Sheet view). Wraps the /audit/website/* endpoints with typed
 * fetchers. Mirrors audit.ts patterns — auth, serialization, error extraction.
 */
import { API } from "../config/api"
import {
  AuditRunSummary,
  AuditColumnConfig,
  VehicleResponse,
  AuditColumnsResponse,
} from "./audit"

// Re-export for convenience — consumers only need to import from this file.
export type { AuditRunSummary, AuditColumnConfig, VehicleResponse, AuditColumnsResponse }

// --- Website-specific types ---

export interface WebsiteFilterOptionsResponse {
  rooftops: string[]
  vehicle_types: string[]
  tekion_statuses: string[]
  dispositions: string[]
  age_range: { min: number; max: number }
}

export interface ListWebsiteVehiclesParams {
  sheet: string
  page?: number
  size?: number
  sort?: string
  rooftop?: string
  vehicle_type?: string
  tekion_status?: string
  search?: string
}

export interface WebsiteVehicleListResponse {
  vehicles: VehicleResponse[]
  total_matching: number
  total_unfiltered: number
  page: number
  size: number
  sheet: string
}

// --- Auth helper (local copy matching existing pattern across api files) ---

const TOKEN_KEY = "invi_token"

function authHeaders(): HeadersInit {
  const token = localStorage.getItem(TOKEN_KEY)
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail = `Request failed: ${res.status}`
    try {
      const body = await res.json()
      if (body.detail) detail = body.detail
    } catch {
      // fall through with generic message
    }
    throw new Error(detail)
  }
  return res.json()
}

// --- Fetchers ---

export async function listWebsiteVehicles(
  runId: number,
  params: ListWebsiteVehiclesParams
): Promise<WebsiteVehicleListResponse> {
  const query = new URLSearchParams()
  query.append("sheet", params.sheet)
  if (params.page !== undefined) query.append("page", String(params.page))
  if (params.size !== undefined) query.append("size", String(params.size))
  if (params.sort) query.append("sort", params.sort)
  if (params.rooftop) query.append("rooftop", params.rooftop)
  if (params.vehicle_type) query.append("vehicle_type", params.vehicle_type)
  if (params.tekion_status) query.append("tekion_status", params.tekion_status)
  if (params.search) query.append("search", params.search)

  const res = await fetch(
    `${API.baseUrl}${API.endpoints.websiteAuditBase}/runs/${runId}/vehicles?${query.toString()}`,
    { headers: authHeaders() }
  )
  return handleResponse<WebsiteVehicleListResponse>(res)
}

export async function getWebsiteVehicle(
  runId: number,
  vehicleId: number
): Promise<VehicleResponse> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.websiteAuditBase}/runs/${runId}/vehicles/${vehicleId}`,
    { headers: authHeaders() }
  )
  return handleResponse<VehicleResponse>(res)
}

export async function getWebsiteFilterOptions(
  runId: number,
  sheet: string
): Promise<WebsiteFilterOptionsResponse> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.websiteAuditBase}/runs/${runId}/filter-options?sheet=${encodeURIComponent(sheet)}`,
    { headers: authHeaders() }
  )
  return handleResponse<WebsiteFilterOptionsResponse>(res)
}

export async function getWebsiteColumnConfig(
  sheetSlug: string
): Promise<AuditColumnsResponse> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.websiteAuditBase}/columns/${encodeURIComponent(sheetSlug)}`,
    { headers: authHeaders() }
  )
  return handleResponse<AuditColumnsResponse>(res)
}

export async function exportWebsiteVehicles(
  runId: number,
  params: {
    sheet: string
    sort?: string
    rooftop?: string
    vehicle_type?: string
    tekion_status?: string
    search?: string
  }
): Promise<Blob> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.websiteAuditBase}/runs/${runId}/export`,
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(params),
    }
  )
  if (!res.ok) {
    let detail = `Export failed: ${res.status}`
    try {
      const body = await res.json()
      if (body.detail) detail = body.detail
    } catch {
      // fall through
    }
    throw new Error(detail)
  }
  return res.blob()
}

export async function exportFullWebsiteAudit(runId: number): Promise<Blob> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.websiteAuditBase}/runs/${runId}/export-full`,
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({}),
    }
  )
  if (!res.ok) {
    let detail = `Full export failed: ${res.status}`
    try {
      const body = await res.json()
      if (body.detail) detail = body.detail
    } catch {
      // fall through
    }
    throw new Error(detail)
  }
  return res.blob()
}

// --- Report builder: per-group website audit generation ---

export interface WebsiteAuditRunResult {
  status: string
  matched: number
  missing: number
  should_remove: number
  audit_run_id: number | null
}

// Generate the website audit for the active group. No file upload — the backend
// scrapes the live site and chains off this group's latest inventory-audit
// workbook (so a 400 means "run the Inventory Audit first").
export async function runWebsiteAudit(
  dealerGroupId: number,
): Promise<WebsiteAuditRunResult> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.websiteAuditRun}?dealer_group_id=${dealerGroupId}`,
    { method: "POST", headers: authHeaders() },
  )
  return handleResponse<WebsiteAuditRunResult>(res)
}
